import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  TrendingDown,
  ShieldAlert,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MessageSquare,
  Download,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

type RiskLevel = "low" | "medium" | "high" | "critical";

const RISK_CONFIG: Record<RiskLevel, { label: string; rowClass: string; badgeClass: string; icon: any }> = {
  critical: {
    label: "Critique",
    rowClass: "bg-destructive/5 hover:bg-destructive/10",
    badgeClass: "bg-destructive/10 text-destructive border-destructive/20",
    icon: ShieldAlert,
  },
  high: {
    label: "Élevé",
    rowClass: "bg-warning/5 hover:bg-warning/10",
    badgeClass: "bg-warning/10 text-warning border-warning/20",
    icon: AlertTriangle,
  },
  medium: {
    label: "Moyen",
    rowClass: "bg-warning/5 hover:bg-warning/10",
    badgeClass: "bg-warning/10 text-warning border-warning/20",
    icon: TrendingDown,
  },
  low: {
    label: "Faible",
    rowClass: "hover:bg-muted/30",
    badgeClass: "bg-success/10 text-success border-success/20",
    icon: CheckCircle2,
  },
};

function RiskBadge({ level }: { level: RiskLevel }) {
  const config = RISK_CONFIG[level];
  const Icon = config.icon;
  return (
    <Badge className={`gap-1 border ${config.badgeClass}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function ScoreMeter({ score }: { score: number }) {
  const level: RiskLevel =
    score >= 75 ? "critical" : score >= 50 ? "high" : score >= 25 ? "medium" : "low";
  const barColor =
    level === "critical"
      ? "bg-destructive"
      : level === "high"
      ? "bg-warning"
      : level === "medium"
      ? "bg-warning"
      : "bg-success";
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-bold w-8 text-right">{score}</span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden w-20">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

const LIMIT = 50;

export default function ChurnRisk() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.staffRole === "SUPER_ADMIN";
  const [riskFilter, setRiskFilter] = useState<RiskLevel | "all">("all");
  const [offset, setOffset] = useState(0);

  const exportMutation = trpc.admin.analytics.exportChurnRisk.useMutation({
    onSuccess: (data: { filename: string; csv: string; count: number }) => {
      const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export tél��chargé — ${data.count} entreprises`);
    },
    onError: () => toast.error("Erreur lors de l'export"),
  });

  const { data, isLoading, isFetching } = trpc.admin.churnRisk.getAtRiskCompanies.useQuery({
    riskLevel: riskFilter !== "all" ? riskFilter : undefined,
    limit: LIMIT,
    offset,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const summary = data?.summary;
  const totalPages = Math.ceil(total / LIMIT) || 1;
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-warning" />
              Churn Risk
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Clients SaaS à risque de désabonnement — scoring automatique par signaux d'alerte
            </p>
          </div>
          {isSuperAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportMutation.mutate({})}
              disabled={exportMutation.isPending}
            >
              <Download className="h-4 w-4 mr-2" />
              {exportMutation.isPending ? "Export…" : "Export CSV"}
            </Button>
          )}
        </div>

        {/* Summary KPI cards */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {(["critical", "high", "medium", "low"] as RiskLevel[]).map((level) => {
            const config = RISK_CONFIG[level];
            const Icon = config.icon;
            return (
              <Card
                key={level}
                className={`cursor-pointer transition-all ${riskFilter === level ? "ring-2 ring-primary" : ""}`}
                onClick={() => {
                  setRiskFilter(riskFilter === level ? "all" : level);
                  setOffset(0);
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {config.label}
                  </CardTitle>
                  <Icon className={`h-4 w-4 ${level === "critical" ? "text-destructive" : level === "high" ? "text-warning" : level === "medium" ? "text-warning" : "text-success"}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {isLoading ? "—" : (summary?.[level] ?? 0)}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              Clients à risque
              {total > 0 && (
                <span className="ml-2 text-sm text-muted-foreground font-normal">({total})</span>
              )}
            </CardTitle>
            <Select
              value={riskFilter}
              onValueChange={(v) => {
                setRiskFilter(v as RiskLevel | "all");
                setOffset(0);
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Niveau de risque" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les niveaux</SelectItem>
                <SelectItem value="critical">Critique</SelectItem>
                <SelectItem value="high">Élevé</SelectItem>
                <SelectItem value="medium">Moyen</SelectItem>
                <SelectItem value="low">Faible</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entreprise</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Niveau</TableHead>
                  <TableHead>Signaux actifs</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                      Calcul du scoring en cours...
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                      Aucun client à risque trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => {
                    const rowConfig = RISK_CONFIG[item.riskLevel as RiskLevel] ?? RISK_CONFIG.low;
                    return (
                      <TableRow
                        key={item.companyId}
                        className={`${rowConfig.rowClass} ${isFetching ? "opacity-60" : ""}`}
                      >
                        <TableCell>
                          <button
                            type="button"
                            className="font-medium text-left hover:underline flex items-center gap-1"
                            onClick={() => navigate(`/companies/${item.companyId}`)}
                          >
                            {item.companyName}
                            <ExternalLink className="h-3 w-3 text-muted-foreground" />
                          </button>
                        </TableCell>
                        <TableCell>
                          <ScoreMeter score={item.riskScore} />
                        </TableCell>
                        <TableCell>
                          <RiskBadge level={item.riskLevel as RiskLevel} />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {item.signals.map((signal) => (
                              <Badge
                                key={signal}
                                variant="outline"
                                className="text-xs font-normal"
                              >
                                {signal}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs capitalize">
                            {item.plan}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/companies/${item.companyId}`)}
                          >
                            <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                            Contacter
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} / {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => setOffset((o) => o + LIMIT)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
